import { Badge } from '@deepguide-ai/ui/badge';
import { cn } from '@/lib/utils';
import type { Plan } from '@deepguide-ai/types';
import { Crown, Sparkles, Users, Building2, Zap } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { trackPlanBadgeViewed } from '@/utils/analytics/planRestrictionEvents';

export interface PlanBadgeProps {
  plan: Plan;
  variant?: 'default' | 'compact' | 'dot';
  showIcon?: boolean;
  className?: string;
  source?: string; // Analytics tracking source
  feature?: string; // Feature name for analytics
}

// Plan colors from RFC-077
const PLAN_COLORS = {
  FREE: {
    bg: 'bg-gray-100 dark:bg-gray-800',
    text: 'text-gray-700 dark:text-gray-300',
    border: 'border-gray-300 dark:border-gray-600',
  },
  STARTER: {
    bg: 'bg-blue-50 dark:bg-blue-950',
    text: 'text-blue-700 dark:text-blue-300',
    border: 'border-blue-200 dark:border-blue-800',
  },
  TEAM: {
    bg: 'bg-purple-50 dark:bg-purple-950',
    text: 'text-status-purple dark:text-purple-300',
    border: 'border-purple-200 dark:border-purple-800',
  },
  PRO: {
    bg: 'bg-amber-50 dark:bg-amber-950',
    text: 'text-status-orange-strong',
    border: 'border-amber-200 dark:border-amber-700',
  },
  ENTERPRISE: {
    bg: 'bg-green-50 dark:bg-green-950',
    text: 'text-green-700 dark:text-green-300',
    border: 'border-green-200 dark:border-green-800',
  },
  CUSTOM: {
    bg: 'bg-slate-50 dark:bg-slate-900',
    text: 'text-slate-700 dark:text-slate-300',
    border: 'border-slate-200 dark:border-slate-700',
  },
} as const;

const PLAN_ICONS = {
  FREE: Sparkles,
  STARTER: Zap,
  TEAM: Users,
  PRO: Crown,
  ENTERPRISE: Building2,
  CUSTOM: Building2,
} as const;

const PLAN_LABELS = {
  FREE: 'Free',
  STARTER: 'Starter',
  TEAM: 'Team',
  PRO: 'Pro',
  ENTERPRISE: 'Enterprise',
  CUSTOM: 'Custom',
} as const;

/**
 * Display plan requirement inline with feature names
 *
 * @example
 * ```tsx
 * // Default - Full badge
 * <PlanBadge plan="PRO" />
 * // Output: "PRO Plan" badge with distinctive color
 *
 * // Compact - Short form
 * <PlanBadge plan="PRO" variant="compact" />
 * // Output: "PRO" badge
 *
 * // Dot - Minimal indicator
 * <PlanBadge plan="PRO" variant="dot" />
 * // Output: Small colored dot
 * ```
 */
export function PlanBadge({
  plan,
  variant = 'default',
  showIcon = true,
  className,
  source = 'unknown',
  feature,
}: PlanBadgeProps) {
  const colors = PLAN_COLORS[plan] || PLAN_COLORS.FREE;
  const Icon = PLAN_ICONS[plan];
  const label = PLAN_LABELS[plan];
  const hasTracked = useRef(false);

  // Track badge view (once per mount)
  useEffect(() => {
    if (!hasTracked.current) {
      trackPlanBadgeViewed({
        plan,
        variant,
        source,
        feature,
      });
      hasTracked.current = true;
    }
  }, [plan, variant, source, feature]);

  // Dot variant - minimal indicator
  if (variant === 'dot') {
    return (
      <div
        className={cn(
          'h-2 w-2 rounded-full',
          colors.bg,
          colors.border,
          'border',
          className
        )}
        title={`${label} Plan`}
      />
    );
  }

  // Compact variant - short form
  if (variant === 'compact') {
    return (
      <Badge
        variant="outline"
        className={cn(
          'text-xs',
          colors.bg,
          colors.text,
          colors.border,
          className
        )}
      >
        {showIcon && Icon && <Icon className="mr-1 h-3 w-3" />}
        {label}
      </Badge>
    );
  }

  // Default variant - full badge
  return (
    <Badge
      variant="outline"
      className={cn(
        'text-xs font-medium',
        colors.bg,
        colors.text,
        colors.border,
        className
      )}
    >
      {showIcon && Icon && <Icon className="mr-1.5 h-3.5 w-3.5" />}
      {label} Plan
    </Badge>
  );
}
